<template>
    <Component
        :is="linkHref ? 'a' : 'div'"
        :id="id"
        :href="linkHref"
        :target="linkTarget"
        class="profile-card"
        :class="{
            [`profile-card--${borderStyle}`]: true,
            'profile-card--link': !!linkHref,
            'profile-card--skeleton': loading,
            'profile-card--no-skills': !hasSkills,
        }"
        :aria-label="altText"
    >
        <VJoyWrapper no-margin :column-gap="columnGap" row-gap="4" wrap="nowrap" align="stretch" class="profile-card__container">
            <div class="profile-card__header">
                <VJoyWrapper no-margin column-gap="3" wrap="nowrap" align="flex-start">
                    <LandscapeProfilePhoto v-if="!loading" :photo-url="profile.photoUrl" :full-name="fullName" :alt="altText" />

                    <VJoyWrapper no-margin row-gap="1" direction="column" align="flex-start">
                        <div>
                            <slot v-if="$slots['top-name']" name="top-name" />
                            <ProfileName
                                :first-name="profile.firstName"
                                :last-name-normalized="profile.lastNameNormalized"
                                :badges="profile.badges"
                                :vertical="profile.vertical"
                            >
                                <slot v-if="$slots['extra-name']" name="extra-name" />
                            </ProfileName>
                            <ProfileHeadline v-if="profile.headline" :headline="profile.headline" />
                        </div>

                        <ProfileInformation
                            :price="profile.price"
                            :location="profile.location"
                            :stats="profile.stats"
                            :global-matching-enabled="!!globalMatchingEnabled"
                            :first-name="profile.firstName"
                            :target-city="profile.targetCity"
                            compact-mode
                            :tooltip-price="tooltipPrice"
                        />
                    </VJoyWrapper>
                </VJoyWrapper>

                <VJoyWrapper
                    v-if="$slots['main-body']"
                    no-margin
                    row-gap="1"
                    column-gap="1"
                    direction="column"
                    align="flex-start"
                    class="joy-mt-4 profile-card__main-body"
                >
                    <slot name="main-body" />
                </VJoyWrapper>
            </div>

            <ProfileSkills v-if="hasSkills" :skills="profile.skills" class="profile-card__skills" />

            <VJoyWrapper
                v-if="$slots['secondary-body']"
                no-margin
                row-gap="1"
                column-gap="1"
                direction="column"
                align="flex-start"
                class="profile-card__secondary-body"
            >
                <slot name="secondary-body" />
            </VJoyWrapper>

            <div v-if="$slots['actions']" class="profile-card__actions">
                <slot name="actions" />
            </div>
        </VJoyWrapper>
    </Component>
</template>

<script setup lang="ts">
    import {computed, type VNode} from 'vue';
    import {useDisplayMobile} from '@malt/utils';
    import {VJoyWrapper} from '@maltjoy/core-vue';
    import LandscapeProfilePhoto from './LandscapeProfilePhoto.vue';
    import ProfileHeadline from '../common/ProfileHeadline.vue';
    import ProfileInformation from '../common/ProfileInformation.vue';
    import ProfileName from '../common/ProfileName.vue';
    import ProfileSkills from '../common/ProfileSkills.vue';
    import type {ProfileCardProps} from '../types';

    const props = defineProps<ProfileCardProps>();

    defineSlots<{
        'top-name'?: () => VNode;
        'extra-name'?: () => VNode;
        'main-body'?: () => VNode;
        'secondary-body'?: () => VNode;
        actions?: () => VNode[];
    }>();

    const {isDisplayMobile} = useDisplayMobile();
    const fullName = computed(() => `${props.profile.firstName} ${props.profile.lastNameNormalized}`);
    const altText = computed(() => `${fullName.value} - ${props.profile.headline}`);
    const hasSkills = computed(() => props.profile.skills.length > 0);
    const columnGap = computed(() => (isDisplayMobile.value ? '4' : '6'));
</script>

<style lang="scss" scoped>
    @keyframes shine {
        to {
            background-position-x: -200%;
        }
    }

    .profile-card {
        container-type: inline-size;
        container-name: profile-card;

        display: block;
        flex: 1 0 auto;
        background-color: var(--joy-color-neutral-0);
        border-radius: var(--joy-core-radius-4);
        border: 1px solid var(--joy-color-neutral-30);
        text-decoration: none;
        color: inherit;

        &__container {
            display: grid;
            grid-template-areas: 'header skills secondary-body actions';
            grid-template-columns: minmax(290px, 350px) minmax(0, 200px) minmax(300px, 1fr) min-content;
            align-items: start;
            padding: var(--joy-core-spacing-4);

            @container profile-card (max-width: 1156px) {
                grid-template-areas:
                    'header skills secondary-body'
                    'actions actions actions';
                grid-template-columns: minmax(290px, 350px) minmax(0, 200px) 1fr;
            }

            @container profile-card (max-width: 944px) {
                grid-template-areas:
                    'header secondary-body'
                    'actions actions';
                grid-template-columns: minmax(290px, 350px) 1fr;
            }

            @container profile-card (max-width: 710px) {
                grid-template-areas:
                    'header'
                    'secondary-body'
                    'skills'
                    'actions';
                grid-template-columns: 1fr;
            }
        }

        &__header {
            grid-area: header;
            min-width: 0;
            max-width: 350px;

            @container profile-card (max-width: 710px) {
                max-width: none;
            }
        }

        &__main-body {
            @container profile-card (max-width: 710px) {
                margin-left: 0;
            }
        }

        &__secondary-body {
            grid-area: secondary-body;
            min-width: 0;
            max-width: 450px;

            @container profile-card (max-width: 710px) {
                max-width: none;
            }
        }

        &__skills {
            grid-area: skills;
            width: 200px;
            max-height: 76px;
            overflow: hidden;

            @container profile-card (max-width: 944px) {
                display: none;
            }

            @container profile-card (max-width: 710px) {
                display: flex;
                width: 100%;
            }
        }

        &__actions {
            grid-area: actions;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            @container profile-card (min-width: 1156px) {
                padding-left: var(--joy-core-spacing-2);
            }
        }

        &--ADMIN {
            border: 1px solid var(--joy-color-quaternary-50);
        }

        &--SELECTED {
            border: 1px solid var(--joy-color-secondary-50);
        }

        &--link {
            transition: box-shadow var(--joy-transition-duration-short);
        }

        &--link:hover {
            box-shadow: var(--joy-core-elevation-2);
        }

        &--skeleton {
            background-color: var(--joy-color-neutral-10);

            .profile-card__header {
                background: linear-gradient(110deg, var(--joy-color-neutral-20) 8%, var(--joy-color-neutral-10) 18%, var(--joy-color-neutral-20) 33%);
                background-size: 200% 100%;
                animation: 1.5s shine linear infinite;
            }

            .profile-card__body,
            .profile-card__header > * {
                visibility: hidden;
            }
        }
    }
</style>
